import React, { useCallback, useLayoutEffect, useState } from 'react';
import {
  useAsyncValue,
  useNavigate,
  useParams,
  useRevalidator,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';
import { MultimediaCheckBoxTable } from '../../../../../components/display/tables/MultimediaCheckBoxTable';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { dataTypes } from '../../../../../components/display/tables/MultimediaCheckBoxTable/types';
import { OrderType } from '../../../../../types/orders';

// Redux
import { getCursor } from '../../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { openModal } from '../../../../../state/actions/ui/modal';
import { store } from '../../../../../state/configureStore';
import { modalIds } from '../../../../../components/modals/SuperModal/types';
import { roidOTSelected } from '../../../../../state/actions/ui/roidOTSelectedGeneralView';

// Styled
// Fix ancho (2026-07-08): al volverse standalone, esta página es un flex item
// directo del `SubContainer` del layout (fila: NavContainer 36rem + Outlet).
// El Container local (`width: 100%`) resolvía contra el ancho COMPLETO del
// SubContainer y el contenido intrínseco (card de filtros + tabla) desbordaba
// el viewport sin que los filtros hicieran wrap. El canónico de work-orders
// (`WorkOrderDetailed/MultimediaPage`) usa el Container compartido de su
// MainStyledComponents — `width: calc(100% - 40rem)` (nav 36rem + gap) con
// colapso a 100% bajo smallDesktop — que sí constriñe el flex item. Mismo
// import compartido (precedente cross-módulo: páginas de reporting).
import {
  Container,
  ContainerButton,
} from '../../../workOrders/components/MainStyledComponents';
import { InfoMessage } from '../../components/MainStyledComponents';

// ═══════════════════════════════════════════════════════════════════
// V4 P7 FR-7 — Página standalone Multimèdia del RV (2026-07-08: dejó de
// ser tab, ver RouteDetailed/index.tsx).
// ─────────────────────────────────────────────────────────────────
// Reusa `MultimediaCheckBoxTable` (mismo componente que work-orders) con un
// loader que filtra solo Actuacions basica (`OrderType.Action`) hijas del RV.
// Patron de cursor + filtros + paginación idéntico a `OrderMultimediaPage`.
// ═══════════════════════════════════════════════════════════════════

export default function RouteMultimediaPage(): JSX.Element {
  const { t } = useTranslation(['pages', 'workOrders']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const data = useAsyncValue() as dataTypes;
  const { routeId } = useParams<{ routeId: string }>();
  const [selectedRows, setSelectedRows] = useState<Array<string | undefined>>(
    [],
  );
  // Fix 2026-07-08: namespaced al RV — antes ambos destinos (double-tap de
  // fila dentro de la tabla + este botón) hardcodeaban `/work-orders/multimedia`
  // y sacaban al usuario del módulo Recorregut de Vigilància (sidebar +
  // breadcrumb saltaban a "Órdenes de Trabajo"). Ver ruta hermana
  // `multimedia/:id` bajo `surveillance-route/:routeId` en router/index.tsx.
  const multimediaBasePath = `/surveillance-route/${routeId}/multimedia`;

  // Fix 2026-07-08: esta página ahora es HERMANA del wrapper de tabs
  // (RouteDetailedPage), no una tab dentro de él — ya no se beneficia del
  // useEffect que ese wrapper dispatchaba para pintar el breadcrumb "Su.X"
  // (ver RouteDetailed/index.tsx). Replica aquí el mismo patrón que
  // `OrderMultimediaPage` (work-orders/campaigns) usa para su propia
  // página standalone de multimedia: dispatch con el roid resuelto por el
  // loader (`parentOrderRoid`, el Order 1:1 del RV) + el id de la ruta.
  useLayoutEffect(() => {
    if (data?.parentOrderRoid && routeId) {
      dispatch(
        roidOTSelected(OrderType.SurveillanceRoute, data.parentOrderRoid, routeId),
      );
    }
  }, [data?.parentOrderRoid, routeId, dispatch]);

  const handleSeeOrder = useCallback(() => {
    if (selectedRows[0]) {
      navigate(`${multimediaBasePath}/${selectedRows[0]}`);
    }
  }, [selectedRows, navigate, multimediaBasePath]);

  const handleDownloadPhotos = useCallback(() => {
    dispatch(
      openModal(modalIds.DOWNLOAD_MULTIMEDIA_PHOTOS, { selectedRows }),
    );
  }, [selectedRows, dispatch]);

  const onReFetchData = useCallback(async () => {
    const currentCursor = getCursor(tableIds.MULTIMEDIA)(store.getState());
    const setCursorData = {
      take: takeBase,
      isMultimedia: true,
      orderBy: 'desc',
      cursor: '',
      state: currentCursor?.state,
      type: currentCursor?.type,
      fromDate: currentCursor?.fromDate,
      toDate: currentCursor?.toDate,
      indexType: currentCursor?.indexType,
      indexExpedition: currentCursor?.indexExpedition,
      classification: currentCursor?.classification,
      predefinedOrderId: currentCursor?.predefinedOrderId,
      roadId: currentCursor?.roadId,
      concessionId: currentCursor?.concessionId,
      capitolId: currentCursor?.capitolId,
      subCapitolId: currentCursor?.subCapitolId,
      pkInitKm: currentCursor?.pkInitKm,
      pkInitMeter: currentCursor?.pkInitMeter,
      pkEndKm: currentCursor?.pkEndKm,
      pkEndMeter: currentCursor?.pkEndMeter,
    };
    dispatch(setCursor(tableIds.MULTIMEDIA, setCursorData));
    revalidator.revalidate();
  }, [dispatch, revalidator]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, [revalidator]);

  // Empty state si no hay multimedia (todavía no se ha tomado ninguna foto en
  // las Actuacions basica del RV).
  if (!data || data.totalCount === 0) {
    return (
      <Container>
        <InfoMessage>{t('pages:surveillance.childOrders.noMultimedia')}</InfoMessage>
      </Container>
    );
  }

  return (
    <Container>
      <MultimediaCheckBoxTable
        idCss="route-multimedia"
        titleText={t('pages:workOrders.multimedia')}
        data={data}
        setRowState={setSelectedRows}
        rowState={selectedRows}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableIds.MULTIMEDIA}
        multimediaBasePath={multimediaBasePath}
      />
      <ContainerButton>
        <MainButton
          variant="secondary"
          text={t('pages:workOrders.viewPhotos')}
          disabled={!(selectedRows.length > 0 && selectedRows.length < 2)}
          onClick={handleSeeOrder}
        />
        <MainButton
          text={t('pages:workOrders.downloadPhotos')}
          disabled={selectedRows.length === 0}
          onClick={handleDownloadPhotos}
        />
      </ContainerButton>
    </Container>
  );
}
